import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api';
import { useCart } from '../../context/CartContext';
import ProductDetail from './ProductDetail';

export default function StandMenu() {
  const { id } = useParams();
  const [stand, setStand] = useState(null);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const { dispatch, count, total } = useCart();
  const [addedId, setAddedId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [helpSent, setHelpSent] = useState(false);
  const [helpLoading, setHelpLoading] = useState(false);
  const categoryRefs = useRef({});

  useEffect(() => {
    api.getStand(id).then(setStand);
    api.getStandProducts(id, true).then(p => {
      setProducts(p);
      if (p.length > 0) setActiveCategory(p[0].categorie);
    });
  }, [id]);

  const categories = [...new Set(products.map(p => p.categorie))];

  const handleAdd = (product) => {
    dispatch({ type: 'ADD', product });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 600);
  };

  const handleHelp = async () => {
    setHelpLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/stands/${id}/help`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: 'Un client souhaite des conseils' }),
      });
      setHelpSent(true);
      setTimeout(() => setHelpSent(false), 5000);
    } catch (e) {}
    setHelpLoading(false);
  };

  const scrollToCategory = (cat) => {
    setActiveCategory(cat);
    categoryRefs.current[cat]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!stand) return (
    <div className="flex items-center justify-center min-h-screen bg-[#FEFCF8]">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C4673D] border-t-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FEFCF8]">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#F3DFC0] shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/order" className="text-[#8B7355] hover:text-[#332A1C] transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="font-serif font-bold text-xl text-[#332A1C]">
              {stand.nom}
            </h1>
          </div>
          <Link
            to="/cart"
            className="relative flex items-center gap-2 bg-white border border-[#F3DFC0] text-[#332A1C] px-4 py-2 rounded-full text-sm font-medium hover:border-[#C4673D]/40 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4 text-[#8B7355]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="text-[#332A1C] font-medium">{total.toFixed(2)} &euro;</span>
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C4673D] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
        </div>

        {/* Category Tabs */}
        <div className="max-w-lg mx-auto overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-4 pb-3">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => scrollToCategory(cat)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? 'bg-[#C4673D] text-white shadow-sm'
                    : 'bg-white text-[#8B7355] border border-[#E2D1B8] hover:border-[#C4673D]/40 hover:text-[#332A1C]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Help banner */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        {helpSent ? (
          <div className="bg-[#6B7F3B]/10 border border-[#6B7F3B]/30 text-[#6B7F3B] rounded-xl px-4 py-3 text-sm text-center">
            ✓ Votre demande a été envoyée ! Un membre de l'équipe viendra vous conseiller.
          </div>
        ) : (
          <button
            onClick={handleHelp}
            disabled={helpLoading}
            className="w-full bg-white border border-[#F3DFC0] rounded-xl px-4 py-3 text-sm text-[#8B7355] hover:border-[#C4673D] hover:text-[#C4673D] transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
            </svg>
            {helpLoading ? 'Envoi...' : 'Besoin de conseil ? Demandez à l\'équipe du stand'}
          </button>
        )}
      </div>

      {/* Products */}
      <div className="max-w-lg mx-auto px-4 py-6 space-y-8 pb-24">
        {categories.map(cat => (
          <div key={cat} ref={el => categoryRefs.current[cat] = el} className="scroll-mt-28">
            {/* Category Title */}
            <div className="mb-4 pt-2">
              <h2 className="font-serif text-lg text-[#332A1C] mb-1.5">
                {cat}
              </h2>
              <div className="w-10 h-0.5 bg-[#C4673D]/30 rounded-full" />
            </div>

            <div className="space-y-3">
              {products.filter(p => p.categorie === cat).map(product => (
                <div
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className="bg-white rounded-2xl border border-[#F3DFC0]/60 shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer"
                >
                  <div className="flex gap-3 p-3 items-center">
                    {/* Product image */}
                    {product.image_url && (
                      <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden">
                        <img
                          src={product.image_url}
                          alt={product.nom}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                    {/* Product info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#332A1C] text-sm">{product.nom}</h3>
                      {product.description && (
                        <p className="text-xs text-[#8B7355] mt-0.5 line-clamp-2 leading-relaxed">
                          {product.description}
                        </p>
                      )}
                      <p className="text-[#C4673D] font-bold mt-1">
                        {product.prix_ttc.toFixed(2)} &euro;
                      </p>
                    </div>
                    {/* Add button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAdd(product); }}
                      className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-bold transition-all duration-200 shadow-sm ${
                        addedId === product.id
                          ? 'bg-[#6B7F3B] text-white scale-110'
                          : 'bg-[#C4673D] text-white hover:bg-[#A8532E] hover:scale-105 active:scale-95'
                      }`}
                    >
                      {addedId === product.id ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selectedProduct && (
        <ProductDetail
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
